import Store from '@ember-data/store';
import Controller from '@ember/controller';
import { action } from '@ember/object';
import Router from '@ember/routing/router';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import logger from 'donor-elf-web/lib/logger';
import FileUploadInfoModel from 'donor-elf-web/models/file-upload-info';
import type { ImportAgUsaRouteModel } from 'donor-elf-web/routes/funds/fund/import/ag-usa';
import ContactsCache from 'donor-elf-web/services/contacts-cache';

export default class DashboardController extends Controller {
  model!: ImportAgUsaRouteModel;

  @service declare contactsCache: ContactsCache;
  @service declare store: Store;
  @service declare router: Router;

  file?: File;
  @tracked uploadFile = false;
  @tracked fileUploadInfo?: FileUploadInfoModel;
  @tracked fileUploadProgressPercent: number = 0;
  @tracked fileUploadProgressBackgroundClass: string = 'bg-brand';
  @tracked processFile = false;
  @tracked fundUploadProgressPercent: number = 0;
  @tracked fundUploadProgressBackgroundClass: string = 'bg-brand';
  @tracked errorMessage?: string;
  @tracked error: any;

  get mappedAccountNumber() {
    return this.model.agUsaImport?.mappedAccountNumber;
  }

  get exampleAccountNumber() {
    return this.mappedAccountNumber || '123456';
  }

  get validDailyFileName() {
    return this.file!.name.match(/DailyFaithPromiseCashReceipt.xls$/);
  }

  get validYtdFileName() {
    return this.file!.name.match(
      /missionary.*faith.*promise.*and.*cash.*receipts.*ytd.*\.csv$/i,
    );
  }

  @action
  templateWillDestroy() {
    this.file = undefined;
    this.uploadFile = false;
    this.fileUploadInfo = undefined;
    this.fileUploadProgressPercent = 0;
    this.fileUploadProgressBackgroundClass = 'bg-brand';
    this.fundUploadProgressPercent = 0;
    this.fundUploadProgressBackgroundClass = 'bg-brand';
    this.processFile = false;
  }

  @action
  handleFileInputChanged(e: any) {
    if (e.target.files.length === 1) {
      this.file = e.target.files[0];
      this.errorMessage = undefined;
    } else {
      this.file = undefined;
    }
  }

  @action
  async upload() {
    if (this.uploadFile) {
      return;
    }
    this.errorMessage = undefined;
    if (!this.file) {
      this.errorMessage = 'Select a file first';
      return;
    }
    if (!this.validDailyFileName && !this.validYtdFileName) {
      this.errorMessage =
        "Invalid file name. Check the example file names below to be sure you're uploading the correct file.";
      return;
    }

    this.fileUploadInfo = await this.store.queryRecord('file-upload-info', {
      filter: { file_name: this.file.name },
    });
    this.fileUploadProgressPercent = 5;
    this.fileUploadProgressBackgroundClass = 'bg-brand';
    this.uploadFile = true;
  }

  @action
  handleFileUploadProgress(percentUploaded: number) {
    this.fileUploadProgressPercent = Math.max(percentUploaded, 5);
  }

  @action
  async handleFileUploadComplete() {
    this.fileUploadProgressPercent = 100;
    setTimeout(() => {
      // delaying changing background color until the width transition completes
      this.fileUploadProgressBackgroundClass = 'bg-success';
    }, 500);

    let startImport = this.store.createRecord('ag-usa-import', {
      fileName: this.fileUploadInfo!.key,
    });
    try {
      await startImport.save();
      this.processFile = true;
    } catch (error) {
      if (error.errors) {
        this.errorMessage = error.errors[0].detail;
      }
      logger.error(error);
    }
  }

  @action
  handleFundUploadProgress(percentUploaded: number) {
    this.fundUploadProgressPercent = Math.max(percentUploaded, 5);
  }

  @action
  async handleFundUploadComplete() {
    this.fundUploadProgressPercent = 100;
    setTimeout(() => {
      // delaying changing background color until the width transition completes
      this.fundUploadProgressBackgroundClass = 'bg-success';
    }, 500);
    await this.contactsCache.loadModifiedContacts();
    this.router.transitionTo('funds.fund.dashboard');
  }
}
